import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  FaEdit, FaEnvelope, FaProjectDiagram,
  FaBuilding, FaHashtag, FaUsers, FaUserFriends, FaLink, FaDollarSign,
  FaExchangeAlt, FaWallet, FaRegStickyNote, FaBalanceScale, FaUserEdit, FaRegClock,
} from 'react-icons/fa';
import { formatNumber } from 'helpers/utils';
import { sendBackUpdateBudgetToHRAdmin } from 'redux/booking/travel/travelBookingSlice';
import { Card, Badge, ActionDropdown, ToggleSwitch } from 'components/common';
import BookingUpdateBudgetModal from './BookingUpdateBudgetModal';

// Leading icon is decorative — the text label already names the field, so it is
// hidden from screen readers rather than read out twice.
const Field = ({ icon: Icon, label, children, danger = false }) => (
  <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-0.5 sm:tw-gap-2 tw-mb-1.5 tw-text-sm">
    <span className="sm:tw-w-40 sm:tw-flex-shrink-0 tw-font-semibold tw-text-slate-600 tw-flex tw-items-start tw-gap-2">
      {Icon && <Icon aria-hidden="true" size={13} className="tw-text-slate-400 tw-flex-shrink-0 tw-mt-0.5" />}
      <span className="tw-min-w-0">{label}</span>
    </span>
    <span className={`${danger ? 'tw-text-red-600' : 'tw-text-blue-700'} tw-min-w-0 tw-break-words`}>{children}</span>
  </div>
);
Field.propTypes = { icon: PropTypes.elementType, label: PropTypes.node, children: PropTypes.node, danger: PropTypes.bool };

const SectionTitle = ({ children }) => (
  <h6 className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-500 tw-mb-3">{children}</h6>
);
SectionTitle.propTypes = { children: PropTypes.node };

// Show a formatted amount, or "—" when the value is missing (null/undefined).
// A real 0 is shown as 0 (it's a valid "no budget" figure).
const money = (value, currency = 'VND') => (value == null ? '—' : `${formatNumber(value, currency)} ${currency}`);

const BookingDetailTripForProject = ({ booking }) => {
  const [showModal, setShowModal] = useState(false);
  const { project } = booking;
  const { allow_edit_project_budget, allow_send_back_update_budget_to_hr_admin } = booking?.permissions || {};
  const dispatch = useDispatch();

  const items = [];
  if (allow_edit_project_budget) items.push({ label: 'Update project budget', icon: <FaEdit />, onClick: () => setShowModal(true) }, { divider: true });
  if (allow_send_back_update_budget_to_hr_admin) items.push({ label: 'Send back to HR admin', icon: <FaEnvelope />, onClick: () => dispatch(sendBackUpdateBudgetToHRAdmin(booking.id)) });

  return (
    <>
      <Card
        padding="none"
        className="tw-mb-3"
        header={<h5 className="tw-mb-0 tw-font-semibold tw-text-slate-800 tw-flex tw-items-center tw-gap-2"><FaProjectDiagram /> Trip for project</h5>}
      >
        <div className="tw-bg-slate-50 tw-p-4">
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6">
            <div>
              <SectionTitle>Project</SectionTitle>
              <Field icon={FaBuilding} label="Customer name">{project?.customer_name}</Field>
              <Field icon={FaProjectDiagram} label="Project name">{project?.project_name}</Field>
              <Field icon={FaHashtag} label="Job number">{booking?.job_number}</Field>
              <Field icon={FaUsers} label="Customers traveler">
                <span className="tw-flex tw-flex-wrap tw-gap-1">
                  {(booking.customers_traveler || []).map((item, i) => <Badge key={i} variant="primary" size="sm">{item}</Badge>)}
                </span>
              </Field>
              <Field icon={FaUserFriends} label="Number of customers traveler">{booking.customers_traveler && booking.customers_traveler.length}</Field>
            </div>
            <div>
              <div className="tw-flex tw-items-center tw-justify-between">
                <SectionTitle>Budget</SectionTitle>
                {booking?.follow_project_budget && items.length > 0 && <ActionDropdown items={items} menuWidth={220} />}
              </div>
              <Field icon={FaLink} label="Follow project budget">
                <ToggleSwitch value={!!booking.follow_project_budget} disabled onChange={() => {}} />
              </Field>
              <Field icon={FaDollarSign} label="Budget currency">{project?.budget_currency || 'VND'}</Field>
              <Field icon={FaExchangeAlt} label="Exchange rate">{project?.budget_exchange_rate == null ? '—' : formatNumber(project.budget_exchange_rate)}</Field>
              <Field icon={FaWallet} label="Budget amount">{money(project?.budget_amount_display, project?.budget_currency || 'VND')}</Field>
              <Field icon={FaRegStickyNote} label="Notes">{project?.budget_notes || '—'}</Field>
              <Field icon={FaBalanceScale} label="Budget remain" danger>{money(project?.budget_remaining_display, project?.budget_currency || 'VND')}</Field>
              <Field icon={FaUserEdit} label="Updated by">{project?.budget_by || '—'}</Field>
              <Field icon={FaRegClock} label="Updated date">{project?.budget_date || '—'}</Field>
            </div>
          </div>
        </div>
      </Card>

      <BookingUpdateBudgetModal showModal={showModal} setShowModal={setShowModal} booking={booking} />
    </>
  );
};

BookingDetailTripForProject.propTypes = {
  booking: PropTypes.object,
};

export default BookingDetailTripForProject;
